import { mapErrorCodes } from '@ps/web-shared-validators';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';

import useErrorHandling from '@swf/core/hooks/useErrorHandling';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardJump from '@swf/core/hooks/useWizardJump';

import { type FirstWizardValues } from '../../types/Cashier';
import useIsCARestrictedState from '../Signup/hooks/useIsCARestrictedState';

const ValidationError: React.FC = () => {
  const form = useForm();
  const {
    data: { fieldsMap },
    values
  } = useWizardContext();

  const {
    errors: [error],
    removeError
  } = useErrorHandling();

  const { jumpBack } = useWizardJump();
  const [jumpRefId, setJumpRefId] = useState('');
  const [fieldsValue, setFieldsValue] = useState<FirstWizardValues>();
  const [currentPageError, setCurrentPageError] = useState(false);
  const { fieldErrors } = error || {};
  const isCARestrictedStateFieldError = useIsCARestrictedState();

  const { values: fieldWithError } = useFormState();

  useEffect(() => {
    if (jumpRefId && error && !currentPageError) {
      jumpBack(jumpRefId);
    }

    return () => {
      if (jumpRefId) {
        setJumpRefId('');
      }
      if (error && currentPageError) {
        removeError(error);
      }
    };
  }, [jumpBack, jumpRefId, error, currentPageError, removeError]);

  useEffect(() => {
    if (fieldErrors?.length) {
      for (const [index, { pageIdRef, fields }] of fieldsMap.entries()) {
        const { fieldName, code, defaultMessage } =
          fieldErrors.find(
            ({ fieldName }) =>
              fields.includes(fieldName) ||
              fields.includes(`deposit.${fieldName}`) ||
              fields.includes(`withdraw.${fieldName}`)
          ) || {};
        if (
          fieldName &&
          fieldsValue &&
          fieldsValue[fieldName as keyof FirstWizardValues] &&
          values[fieldName as keyof FirstWizardValues] !== fieldWithError[fieldName]
        ) {
          form.mutators.setFieldData(
            fields.find(
              (entry) =>
                entry === fieldName ||
                entry === `deposit.${fieldName}` ||
                entry === `withdraw.${fieldName}`
            ),
            {
              warning: ''
            }
          );
          delete fieldsValue[fieldName as keyof FirstWizardValues];
          setFieldsValue(fieldsValue);
          setCurrentPageError(false);
          break;
        }

        if (!jumpRefId && fieldName) {
          if (!isCARestrictedStateFieldError) {
            setJumpRefId(pageIdRef);
          }
          if (index === fieldsMap.length - 1) {
            setCurrentPageError(true);
            setFieldsValue({ ...fieldsValue, ...values });
            form.mutators.setFieldData(
              fields.find(
                (entry) =>
                  entry === fieldName ||
                  entry === `deposit.${fieldName}` ||
                  entry === `withdraw.${fieldName}`
              ),
              {
                warning: defaultMessage || mapErrorCodes(code || ''),
                samePageError: true
              }
            );
          }
          break;
        }
      }
    }
  }, [
    fieldErrors,
    fieldsMap,
    form.mutators,
    jumpRefId,
    fieldWithError,
    fieldsValue,
    values,
    isCARestrictedStateFieldError
  ]);

  return null;
};

export default ValidationError;
